import featureEnabled from 'shared/helper/feature';
import trackRubriqEvent from 'rubriq/helpers/trackRubriqEvent';

/**
 * Common properties included on every Abstract event.
 * Callers pass these in alongside event-specific properties.
 *
 * @typedef {object} AbstractCommonProps
 * @property {string|null} userIdentity - user.identity
 * @property {string|null} planType     - e.g. 'free_trial' | 'premium' | 'preferred_group'
 * @property {string|null} planName     - formatted plan display name
 * @property {string}      trialStatus  - 'trialing' | 'none'
 */

var isAbstractEnabled = function isAbstractEnabled() {
  return featureEnabled('rubriq_abstract');
};
var isAbstractMixpanelEnabled = function isAbstractMixpanelEnabled() {
  return featureEnabled('rubriq_abstract_mixpanel');
};
var formatFileSize = function formatFileSize(bytes) {
  if (bytes == null) return null;
  if (bytes < 1024) return "".concat(bytes, " B");
  if (bytes < 1024 * 1024) return "".concat((bytes / 1024).toFixed(1), " KB");
  return "".concat((bytes / (1024 * 1024)).toFixed(2), " MB");
};

// ─── Abstract Tab Viewed ──────────────────────────────────────────────────────
// Trigger: user clicks the Abstracts tab in the Prepare Document workspace
// is_first_visit defaults to false; caller passes true on the first tab view
// within the session (tracked via sessionStorage).
export var trackAbstractTabViewed = function trackAbstractTabViewed() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$isFirstVisit = _ref.isFirstVisit,
    isFirstVisit = _ref$isFirstVisit === void 0 ? false : _ref$isFirstVisit,
    _ref$userIdentity = _ref.userIdentity,
    userIdentity = _ref$userIdentity === void 0 ? null : _ref$userIdentity,
    _ref$planType = _ref.planType,
    planType = _ref$planType === void 0 ? null : _ref$planType,
    _ref$planName = _ref.planName,
    planName = _ref$planName === void 0 ? null : _ref$planName,
    _ref$trialStatus = _ref.trialStatus,
    trialStatus = _ref$trialStatus === void 0 ? 'none' : _ref$trialStatus;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract Tab Viewed', {
    is_first_visit: isFirstVisit,
    user_identity: userIdentity,
    planType: planType,
    planName: planName,
    trial_status: trialStatus
  });
};

// ─── Abstract Upgrade Prompt Shown ───────────────────────────────────────────
// Trigger: user without qualifying plan clicks Generate/New Abstract and sees
// the upgrade screen. trial_status defaults to 'none'.
export var trackAbstractUpgradePromptShown = function trackAbstractUpgradePromptShown() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref2$trialStatus = _ref2.trialStatus,
    trialStatus = _ref2$trialStatus === void 0 ? 'none' : _ref2$trialStatus,
    _ref2$userIdentity = _ref2.userIdentity,
    userIdentity = _ref2$userIdentity === void 0 ? null : _ref2$userIdentity,
    _ref2$planType = _ref2.planType,
    planType = _ref2$planType === void 0 ? null : _ref2$planType,
    _ref2$planName = _ref2.planName,
    planName = _ref2$planName === void 0 ? null : _ref2$planName;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract Upgrade Prompt Shown', {
    trial_status: trialStatus,
    user_identity: userIdentity,
    planType: planType,
    planName: planName
  });
};

// ─── Abstract Upgrade CTA Clicked ────────────────────────────────────────────
// Trigger: user clicks the upgrade CTA from the Abstract upgrade prompt screen.
export var trackAbstractUpgradeCTAClicked = function trackAbstractUpgradeCTAClicked() {
  var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref3$userIdentity = _ref3.userIdentity,
    userIdentity = _ref3$userIdentity === void 0 ? null : _ref3$userIdentity,
    _ref3$planType = _ref3.planType,
    planType = _ref3$planType === void 0 ? null : _ref3$planType,
    _ref3$planName = _ref3.planName,
    planName = _ref3$planName === void 0 ? null : _ref3$planName,
    _ref3$trialStatus = _ref3.trialStatus,
    trialStatus = _ref3$trialStatus === void 0 ? 'none' : _ref3$trialStatus;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract Upgrade CTA Clicked', {
    user_identity: userIdentity,
    planType: planType,
    planName: planName,
    trial_status: trialStatus
  });
};

// ─── Abstract Initiation Started ─────────────────────────────────────────────
// Trigger: user clicks 'New Abstract' (header) or 'Generate Abstract' (empty state).
// entry_point: 'new_abstract_button' | 'generate_abstract_button'
// Defaults to 'new_abstract_button' to match the spec default.
export var trackAbstractInitiationStarted = function trackAbstractInitiationStarted() {
  var _ref4 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref4$entryPoint = _ref4.entryPoint,
    entryPoint = _ref4$entryPoint === void 0 ? 'new_abstract_button' : _ref4$entryPoint,
    _ref4$userIdentity = _ref4.userIdentity,
    userIdentity = _ref4$userIdentity === void 0 ? null : _ref4$userIdentity,
    _ref4$planType = _ref4.planType,
    planType = _ref4$planType === void 0 ? null : _ref4$planType,
    _ref4$planName = _ref4.planName,
    planName = _ref4$planName === void 0 ? null : _ref4$planName,
    _ref4$trialStatus = _ref4.trialStatus,
    trialStatus = _ref4$trialStatus === void 0 ? 'none' : _ref4$trialStatus;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract Initiation Started', {
    entry_point: entryPoint,
    user_identity: userIdentity,
    planType: planType,
    planName: planName,
    trial_status: trialStatus
  });
};

// ─── Abstract Upload Modal Opened ────────────────────────────────────────────
// Trigger: the upload document modal is displayed to the user.
// entry_point matches the Initiation Started entry point that opened this flow.
export var trackAbstractUploadModalOpened = function trackAbstractUploadModalOpened() {
  var _ref5 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref5$entryPoint = _ref5.entryPoint,
    entryPoint = _ref5$entryPoint === void 0 ? 'new_abstract_button' : _ref5$entryPoint,
    _ref5$userIdentity = _ref5.userIdentity,
    userIdentity = _ref5$userIdentity === void 0 ? null : _ref5$userIdentity,
    _ref5$planType = _ref5.planType,
    planType = _ref5$planType === void 0 ? null : _ref5$planType,
    _ref5$planName = _ref5.planName,
    planName = _ref5$planName === void 0 ? null : _ref5$planName,
    _ref5$trialStatus = _ref5.trialStatus,
    trialStatus = _ref5$trialStatus === void 0 ? 'none' : _ref5$trialStatus;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract Upload Modal Opened', {
    entry_point: entryPoint,
    user_identity: userIdentity,
    planType: planType,
    planName: planName,
    trial_status: trialStatus
  });
};

// ─── Abstract File Selected ───────────────────────────────────────────────────
// Trigger: user selects or drops a file into the upload drop zone.
// file_type: 'doc' | 'docx' | 'pdf' | 'other'; defaults to 'unknown'.
// file_size: number of bytes (or null if unavailable).
// file_identity: the server-assigned identity of the uploaded file.
export var trackAbstractFileSelected = function trackAbstractFileSelected() {
  var _ref6 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref6$fileType = _ref6.fileType,
    fileType = _ref6$fileType === void 0 ? 'unknown' : _ref6$fileType,
    _ref6$fileSize = _ref6.fileSize,
    fileSize = _ref6$fileSize === void 0 ? null : _ref6$fileSize,
    _ref6$fileIdentity = _ref6.fileIdentity,
    fileIdentity = _ref6$fileIdentity === void 0 ? null : _ref6$fileIdentity,
    _ref6$userIdentity = _ref6.userIdentity,
    userIdentity = _ref6$userIdentity === void 0 ? null : _ref6$userIdentity,
    _ref6$planType = _ref6.planType,
    planType = _ref6$planType === void 0 ? null : _ref6$planType,
    _ref6$planName = _ref6.planName,
    planName = _ref6$planName === void 0 ? null : _ref6$planName,
    _ref6$trialStatus = _ref6.trialStatus,
    trialStatus = _ref6$trialStatus === void 0 ? 'none' : _ref6$trialStatus;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract File Selected', {
    file_type: fileType,
    file_size: fileSize,
    file_size_display: formatFileSize(fileSize),
    file_identity: fileIdentity,
    user_identity: userIdentity,
    planType: planType,
    planName: planName,
    trial_status: trialStatus
  });
};

// ─── Abstract File Removed ────────────────────────────────────────────────────
// Trigger: user clicks the trash icon on the uploaded file card inside the Upload modal.
export var trackAbstractFileRemoved = function trackAbstractFileRemoved() {
  var _ref7 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref7$fileIdentity = _ref7.fileIdentity,
    fileIdentity = _ref7$fileIdentity === void 0 ? null : _ref7$fileIdentity,
    _ref7$userIdentity = _ref7.userIdentity,
    userIdentity = _ref7$userIdentity === void 0 ? null : _ref7$userIdentity,
    _ref7$planType = _ref7.planType,
    planType = _ref7$planType === void 0 ? null : _ref7$planType,
    _ref7$planName = _ref7.planName,
    planName = _ref7$planName === void 0 ? null : _ref7$planName,
    _ref7$trialStatus = _ref7.trialStatus,
    trialStatus = _ref7$trialStatus === void 0 ? 'none' : _ref7$trialStatus;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract File Removed', {
    file_identity: fileIdentity,
    user_identity: userIdentity,
    planType: planType,
    planName: planName,
    trial_status: trialStatus
  });
};

// ─── Abstract Upload Error ────────────────────────────────────────────────────
// Trigger: validation fails after user selects a file (wrong format, too long,
// corrupted, or empty).
// error_type: 'unsupported_format' | 'word_limit_exceeded' | 'corrupted_file' | 'empty_file'
// file_type: the type of the file that failed.
export var trackAbstractUploadError = function trackAbstractUploadError() {
  var _ref8 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    errorType = _ref8.errorType,
    _ref8$fileType = _ref8.fileType,
    fileType = _ref8$fileType === void 0 ? 'unknown' : _ref8$fileType,
    _ref8$userIdentity = _ref8.userIdentity,
    userIdentity = _ref8$userIdentity === void 0 ? null : _ref8$userIdentity,
    _ref8$planType = _ref8.planType,
    planType = _ref8$planType === void 0 ? null : _ref8$planType,
    _ref8$planName = _ref8.planName,
    planName = _ref8$planName === void 0 ? null : _ref8$planName,
    _ref8$trialStatus = _ref8.trialStatus,
    trialStatus = _ref8$trialStatus === void 0 ? 'none' : _ref8$trialStatus;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract Upload Error', {
    error_type: errorType,
    file_type: fileType,
    user_identity: userIdentity,
    planType: planType,
    planName: planName,
    trial_status: trialStatus
  });
};

// ─── Abstract Disclaimer Acknowledged ────────────────────────────────────────
// Trigger: user clicks CONTINUE past the manuscript completeness disclaimer.
export var trackAbstractDisclaimerAcknowledged = function trackAbstractDisclaimerAcknowledged() {
  var _ref9 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref9$fileIdentity = _ref9.fileIdentity,
    fileIdentity = _ref9$fileIdentity === void 0 ? null : _ref9$fileIdentity,
    _ref9$userIdentity = _ref9.userIdentity,
    userIdentity = _ref9$userIdentity === void 0 ? null : _ref9$userIdentity,
    _ref9$planType = _ref9.planType,
    planType = _ref9$planType === void 0 ? null : _ref9$planType,
    _ref9$planName = _ref9.planName,
    planName = _ref9$planName === void 0 ? null : _ref9$planName,
    _ref9$trialStatus = _ref9.trialStatus,
    trialStatus = _ref9$trialStatus === void 0 ? 'none' : _ref9$trialStatus;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract Disclaimer Acknowledged', {
    file_identity: fileIdentity,
    user_identity: userIdentity,
    planType: planType,
    planName: planName,
    trial_status: trialStatus
  });
};

// ─── Abstract Generation Started ─────────────────────────────────────────────
// Trigger: user clicks START in the Abstract Settings modal; job is submitted to API.
// abstract_type: 'structured' | 'unstructured'
// word_count_min / word_count_max: integers
// simple_language_summary / graphical_abstract: booleans (default false per spec)
export var trackAbstractGenerationStarted = function trackAbstractGenerationStarted() {
  var _ref10 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    abstractType = _ref10.abstractType,
    wordCountMin = _ref10.wordCountMin,
    wordCountMax = _ref10.wordCountMax,
    _ref10$simpleLanguage = _ref10.simpleLanguageSummary,
    simpleLanguageSummary = _ref10$simpleLanguage === void 0 ? false : _ref10$simpleLanguage,
    _ref10$graphicalAbstr = _ref10.graphicalAbstract,
    graphicalAbstract = _ref10$graphicalAbstr === void 0 ? false : _ref10$graphicalAbstr,
    _ref10$fileIdentity = _ref10.fileIdentity,
    fileIdentity = _ref10$fileIdentity === void 0 ? null : _ref10$fileIdentity,
    _ref10$userIdentity = _ref10.userIdentity,
    userIdentity = _ref10$userIdentity === void 0 ? null : _ref10$userIdentity,
    _ref10$planType = _ref10.planType,
    planType = _ref10$planType === void 0 ? null : _ref10$planType,
    _ref10$planName = _ref10.planName,
    planName = _ref10$planName === void 0 ? null : _ref10$planName,
    _ref10$trialStatus = _ref10.trialStatus,
    trialStatus = _ref10$trialStatus === void 0 ? 'none' : _ref10$trialStatus;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract Generation Started', {
    abstract_type: abstractType,
    word_count_min: wordCountMin,
    word_count_max: wordCountMax,
    simple_language_summary: simpleLanguageSummary,
    graphical_abstract: graphicalAbstract,
    file_identity: fileIdentity,
    user_identity: userIdentity,
    planType: planType,
    planName: planName,
    trial_status: trialStatus
  });
};

// ─── Abstract Generation Completed ───────────────────────────────────────────
// Trigger: API returns a successful response to /api/digital-abstract/create
//   (no error in the response; the job has been accepted for processing).
// abstract_type: 'structured' | 'unstructured'
// word_count_min / word_count_max: integers (the requested range submitted by the user)
// simple_language_summary / graphical_abstract: booleans
export var trackAbstractGenerationCompleted = function trackAbstractGenerationCompleted() {
  var _ref11 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    abstractType = _ref11.abstractType,
    wordCountMin = _ref11.wordCountMin,
    wordCountMax = _ref11.wordCountMax,
    _ref11$simpleLanguage = _ref11.simpleLanguageSummary,
    simpleLanguageSummary = _ref11$simpleLanguage === void 0 ? false : _ref11$simpleLanguage,
    _ref11$graphicalAbstr = _ref11.graphicalAbstract,
    graphicalAbstract = _ref11$graphicalAbstr === void 0 ? false : _ref11$graphicalAbstr,
    _ref11$fileIdentity = _ref11.fileIdentity,
    fileIdentity = _ref11$fileIdentity === void 0 ? null : _ref11$fileIdentity,
    _ref11$userIdentity = _ref11.userIdentity,
    userIdentity = _ref11$userIdentity === void 0 ? null : _ref11$userIdentity,
    _ref11$planType = _ref11.planType,
    planType = _ref11$planType === void 0 ? null : _ref11$planType,
    _ref11$planName = _ref11.planName,
    planName = _ref11$planName === void 0 ? null : _ref11$planName,
    _ref11$trialStatus = _ref11.trialStatus,
    trialStatus = _ref11$trialStatus === void 0 ? 'none' : _ref11$trialStatus;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract Generation Completed', {
    abstract_type: abstractType,
    word_count_min: wordCountMin,
    word_count_max: wordCountMax,
    simple_language_summary: simpleLanguageSummary,
    graphical_abstract: graphicalAbstract,
    file_identity: fileIdentity,
    user_identity: userIdentity,
    planType: planType,
    planName: planName,
    trial_status: trialStatus
  });
};

// ─── Abstract Generation Failed ──────────────────────────────────────────────
// Trigger: async backend processing finishes with status 'failed' — detected
// when a document transitions from 'processing' → 'failed' in the document list.
export var trackAbstractGenerationFailed = function trackAbstractGenerationFailed() {
  var _ref12 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref12$userIdentity = _ref12.userIdentity,
    userIdentity = _ref12$userIdentity === void 0 ? null : _ref12$userIdentity,
    _ref12$planType = _ref12.planType,
    planType = _ref12$planType === void 0 ? null : _ref12$planType,
    _ref12$planName = _ref12.planName,
    planName = _ref12$planName === void 0 ? null : _ref12$planName,
    _ref12$trialStatus = _ref12.trialStatus,
    trialStatus = _ref12$trialStatus === void 0 ? 'none' : _ref12$trialStatus;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract Generation Failed', {
    user_identity: userIdentity,
    planType: planType,
    planName: planName,
    trial_status: trialStatus
  });
};

// ─── Abstract Submission Cancelled ────────────────────────────────────────────
// Trigger: user clicks CANCEL or the × button at any step of the modal flow.
// step: 'upload' | 'disclaimer' | 'settings'
export var trackAbstractSubmissionCancelled = function trackAbstractSubmissionCancelled() {
  var _ref13 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    step = _ref13.step,
    _ref13$fileIdentity = _ref13.fileIdentity,
    fileIdentity = _ref13$fileIdentity === void 0 ? null : _ref13$fileIdentity,
    _ref13$userIdentity = _ref13.userIdentity,
    userIdentity = _ref13$userIdentity === void 0 ? null : _ref13$userIdentity,
    _ref13$planType = _ref13.planType,
    planType = _ref13$planType === void 0 ? null : _ref13$planType,
    _ref13$planName = _ref13.planName,
    planName = _ref13$planName === void 0 ? null : _ref13$planName,
    _ref13$trialStatus = _ref13.trialStatus,
    trialStatus = _ref13$trialStatus === void 0 ? 'none' : _ref13$trialStatus;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract Submission Cancelled', {
    step: step,
    file_identity: fileIdentity,
    user_identity: userIdentity,
    planType: planType,
    planName: planName,
    trial_status: trialStatus
  });
};

// ─── Abstract Output Viewed ───────────────────────────────────────────────────
// Trigger: user clicks 'View' on a completed document card to open the Output view.
// abstract_type: 'structured' | 'unstructured'
// word_count: integer
// optional_outputs_shown: array of strings e.g. ['simple_language_summary']
// Defaults to empty array per spec.
export var trackAbstractOutputViewed = function trackAbstractOutputViewed() {
  var _ref14 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    abstractType = _ref14.abstractType,
    wordCount = _ref14.wordCount,
    _ref14$optionalOutput = _ref14.optionalOutputsShown,
    optionalOutputsShown = _ref14$optionalOutput === void 0 ? [] : _ref14$optionalOutput,
    _ref14$userIdentity = _ref14.userIdentity,
    userIdentity = _ref14$userIdentity === void 0 ? null : _ref14$userIdentity,
    _ref14$planType = _ref14.planType,
    planType = _ref14$planType === void 0 ? null : _ref14$planType,
    _ref14$planName = _ref14.planName,
    planName = _ref14$planName === void 0 ? null : _ref14$planName,
    _ref14$trialStatus = _ref14.trialStatus,
    trialStatus = _ref14$trialStatus === void 0 ? 'none' : _ref14$trialStatus;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract Output Viewed', {
    abstract_type: abstractType,
    word_count: wordCount,
    optional_outputs_shown: optionalOutputsShown,
    user_identity: userIdentity,
    planType: planType,
    planName: planName,
    trial_status: trialStatus
  });
};

// ─── Abstract Content Copied ──────────────────────────────────────────────────
// Trigger: user clicks a Copy button on any section of the Output view.
// section: 'abstract' | 'keywords' | 'simple_language_summary' | 'graphical_abstract' | 'all'
export var trackAbstractContentCopied = function trackAbstractContentCopied() {
  var _ref15 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    section = _ref15.section,
    _ref15$userIdentity = _ref15.userIdentity,
    userIdentity = _ref15$userIdentity === void 0 ? null : _ref15$userIdentity,
    _ref15$planType = _ref15.planType,
    planType = _ref15$planType === void 0 ? null : _ref15$planType,
    _ref15$planName = _ref15.planName,
    planName = _ref15$planName === void 0 ? null : _ref15$planName,
    _ref15$trialStatus = _ref15.trialStatus,
    trialStatus = _ref15$trialStatus === void 0 ? 'none' : _ref15$trialStatus;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract Content Copied', {
    section: section,
    user_identity: userIdentity,
    planType: planType,
    planName: planName,
    trial_status: trialStatus
  });
};

// ─── Abstract Content Downloaded ─────────────────────────────────────────────
// Trigger: user clicks the Download button on any section of the Output view.
// section: 'abstract' | 'keywords' | 'simple_language_summary' | 'graphical_abstract' | 'all'
export var trackAbstractContentDownloaded = function trackAbstractContentDownloaded() {
  var _ref16 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    section = _ref16.section,
    _ref16$userIdentity = _ref16.userIdentity,
    userIdentity = _ref16$userIdentity === void 0 ? null : _ref16$userIdentity,
    _ref16$planType = _ref16.planType,
    planType = _ref16$planType === void 0 ? null : _ref16$planType,
    _ref16$planName = _ref16.planName,
    planName = _ref16$planName === void 0 ? null : _ref16$planName,
    _ref16$trialStatus = _ref16.trialStatus,
    trialStatus = _ref16$trialStatus === void 0 ? 'none' : _ref16$trialStatus;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract Content Downloaded', {
    section: section,
    user_identity: userIdentity,
    planType: planType,
    planName: planName,
    trial_status: trialStatus
  });
};

// ─── Abstract Document Deleted ────────────────────────────────────────────────
// Trigger: user confirms deletion of a processed manuscript from the dashboard.
// status_at_deletion: 'completed' | 'failed'
export var trackAbstractDocumentDeleted = function trackAbstractDocumentDeleted() {
  var _ref17 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    statusAtDeletion = _ref17.statusAtDeletion,
    _ref17$userIdentity = _ref17.userIdentity,
    userIdentity = _ref17$userIdentity === void 0 ? null : _ref17$userIdentity,
    _ref17$planType = _ref17.planType,
    planType = _ref17$planType === void 0 ? null : _ref17$planType,
    _ref17$planName = _ref17.planName,
    planName = _ref17$planName === void 0 ? null : _ref17$planName,
    _ref17$trialStatus = _ref17.trialStatus,
    trialStatus = _ref17$trialStatus === void 0 ? 'none' : _ref17$trialStatus;
  if (!isAbstractMixpanelEnabled()) return;
  trackRubriqEvent('Abstract Document Deleted', {
    status_at_deletion: statusAtDeletion,
    user_identity: userIdentity,
    planType: planType,
    planName: planName,
    trial_status: trialStatus
  });
};